import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Select, SelectContent, SelectItem } from "@/components/ui/Select"
import { SelectTrigger } from "@radix-ui/react-select"
import { PhotoRecord } from "@/lib/catalog"
import { PhotoFlagStatus, PhotoFlagValues } from "@/lib/catalog/types"
import { Icon } from "@/components/icons/Icon"
import clsx from "clsx"
import { ThumbnailSize } from "./Thumbnail"
import { selectedPhotosIDsAtom } from "@/store/photo/photos-state"
import { store } from "@/store/store"
import { setFlag } from "@/store/photo/store-photos"
import { photoFlagKeys } from "@/i18n/dual-role-strings"

const ThumbnailFlag: React.FC<{
	size: ThumbnailSize
	photo: PhotoRecord
	isHovering: boolean
	isMenuOpen: boolean
	shouldShowInlineControls: boolean
	setIsMenuOpen: (open: boolean) => void
}> = ({ size, photo, isHovering, isMenuOpen, shouldShowInlineControls, setIsMenuOpen }) => {
	const { t } = useTranslation("components")
	const iconSize = size === "small" ? 9 : 12

	const handleSelectFlag = useCallback((photoId: string, value: PhotoFlagStatus) => {
		// Don't use the atom value subscription here, it will make the
		// function unstable and force the children to re-render
		const selectedPhotoIDs = store.get(selectedPhotosIDsAtom)
		setFlag(selectedPhotoIDs.has(photoId) ? Array.from(selectedPhotoIDs) : [photoId], value)
	}, [])

	return (
		<Select
			trackingLabel="thumbnail_flag_setter"
			aria-label={t("thumbnailFlag.ariaSetPhotoFlag")}
			value={photo.flag || "unflagged"}
			onValueChange={(value: PhotoFlagStatus) => handleSelectFlag(photo.id, value)}
			onOpenChange={(open) => setIsMenuOpen(open)}
		>
			<SelectTrigger>
				<Icon
					name={photo.flag || "unflagged"}
					size={iconSize}
					className={clsx(
						"my-1 fill-base-300 hover:fill-base-100",
						photo?.flag !== "unflagged" ||
							isHovering ||
							isMenuOpen ||
							shouldShowInlineControls
							? "visible"
							: "invisible",
					)}
				/>
			</SelectTrigger>
			<SelectContent side="top" align="center">
				{PhotoFlagValues.map((flag) => (
					<SelectItem key={flag} value={flag}>
						<div className="text-xs">{t(photoFlagKeys[flag])}</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

export const MemoizedThumbnailFlag = React.memo(ThumbnailFlag, (prev, next) => {
	if (prev.photo.flag !== next.photo.flag) {
		return false
	}

	if (prev.photo.id !== next.photo.id) {
		return false
	}

	if (prev.isHovering !== next.isHovering) {
		return false
	}

	if (prev.isMenuOpen !== next.isMenuOpen) {
		return false
	}

	if (prev.shouldShowInlineControls !== next.shouldShowInlineControls) {
		return false
	}

	if (prev.setIsMenuOpen !== next.setIsMenuOpen) {
		console.error("Potential performance issue in ThumbnailFlag, is setIsMenuOpen stable?")
		return false
	}

	return true
})
